import {
  Alert, App as AntApp, Button, Card, Descriptions, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Switch,
  Table, Tag, Typography,
} from "antd";
import { useCallback, useState } from "react";
import { api, errorText } from "../api";
import { canManage, useSession } from "../session";
import { useResource } from "../useResource";

export type ProactiveTrigger = "DWELL" | "EXIT_INTENT";

export interface ProactiveRule {
  id: string;
  name: string;
  trigger: ProactiveTrigger;
  pageTypes: string[];
  delaySeconds: number;
  message: string;
  enabled: boolean;
}

export interface ProactiveLimits {
  cooldownHours: number;
  maxPerSession: number;
  maxPerDay: number;
}

interface Overview {
  rules: ProactiveRule[];
  limits: ProactiveLimits;
  maxRules: number;
  pageTypes: string[];
}

type RuleDraft = Omit<ProactiveRule, "id">;

const TRIGGERS: Record<ProactiveTrigger, string> = {
  DWELL: "Sayfada kalma",
  EXIT_INTENT: "Çıkış niyeti",
};

const PAGE_LABELS: Record<string, string> = {
  home: "Ana sayfa",
  category: "Kategori",
  product: "Ürün",
  cart: "Sepet",
  other: "Diğer",
};

const EMPTY: RuleDraft = { name: "", trigger: "DWELL", pageTypes: ["product"], delaySeconds: 25, message: "",
  enabled: true };

/**
 * Proaktif açılış (paket 3.6). Mağaza, hangi sayfada ve hangi durumda asistanın kendini kısa bir soruyla tanıtacağını
 * belirler. Mesajlar onaylı şablondur: fiyat, indirim, stok adedi ve aciliyet içeremez (sunucu doğrular). Müşteri
 * balonu kapatırsa belirlenen süre boyunca tekrar gösterilmez.
 */
export function ProactivePage() {
  const me = useSession((s) => s.me);
  const manage = canManage(me);
  const { message } = AntApp.useApp();
  const { data, error, reload } = useResource(useCallback(() => api.get<Overview>("/api/console/proactive"), []));
  const [editing, setEditing] = useState<{ id: string | null; draft: RuleDraft } | null>(null);

  if (error) {
    return <Alert type="error" title={error} showIcon />;
  }
  if (!data) {
    return null;
  }

  async function remove(rule: ProactiveRule) {
    try {
      await api.delete(`/api/console/proactive/rules/${rule.id}`);
      message.success("Kural silindi");
      reload();
    } catch (e) {
      message.error(errorText(e));
    }
  }

  return (
    <Space orientation="vertical" size="large" style={{ width: "100%" }}>
      <Typography.Title level={3}>Proaktif mesajlar</Typography.Title>
      <Typography.Paragraph type="secondary">
        Asistan, burada etkin olan kurallara göre açma butonunun üstünde kısa bir soru gösterir. Müşteri tıklarsa
        sohbet açılır; kapatırsa belirlediğiniz süre boyunca tekrar gösterilmez. Mesajlar fiyat, indirim, stok adedi ve
        aciliyet ifadesi içeremez.
      </Typography.Paragraph>
      <Card title="Kurallar" extra={manage && (
        <Button type="primary" disabled={data.rules.length >= data.maxRules}
          onClick={() => setEditing({ id: null, draft: EMPTY })}>Yeni kural</Button>
      )}>
        <Table<ProactiveRule> rowKey="id" dataSource={data.rules} pagination={false} size="small"
          scroll={{ x: 900 }}
          locale={{ emptyText: "Henüz kural yok; proaktif mesaj gösterilmez" }}
          columns={[
            { title: "Ad", dataIndex: "name", width: 160 },
            { title: "Tetikleyici", key: "trigger", width: 150,
              render: (_: unknown, r) => `${TRIGGERS[r.trigger]} (${r.delaySeconds} sn)` },
            { title: "Sayfalar", dataIndex: "pageTypes", width: 140,
              render: (pages: string[]) => pages.map((p) => <Tag key={p}>{PAGE_LABELS[p] ?? p}</Tag>) },
            { title: "Mesaj", dataIndex: "message" },
            { title: "Durum", dataIndex: "enabled", width: 90,
              render: (on: boolean) => (on ? <Tag color="green">Etkin</Tag> : <Tag>Kapalı</Tag>) },
            ...(manage ? [{
              title: "", key: "actions", width: 170,
              render: (_: unknown, r: ProactiveRule) => (
                <Space>
                  <Button size="small" onClick={() => setEditing({ id: r.id, draft: { ...r } })}>Düzenle</Button>
                  <Popconfirm title="Kural silinsin mi?" okText="Sil" cancelText="Vazgeç" onConfirm={() => remove(r)}>
                    <Button size="small" danger>Sil</Button>
                  </Popconfirm>
                </Space>
              ),
            }] : []),
          ]} />
      </Card>
      <LimitsCard limits={data.limits} manage={manage} onChange={reload} />
      {editing && (
        <RuleModal id={editing.id} draft={editing.draft} pageTypes={data.pageTypes}
          onClose={() => setEditing(null)} onSaved={() => {
            setEditing(null);
            reload();
          }} />
      )}
    </Space>
  );
}

function RuleModal({ id, draft, pageTypes, onClose, onSaved }: {
  id: string | null; draft: RuleDraft; pageTypes: string[]; onClose: () => void; onSaved: () => void;
}) {
  const { message } = AntApp.useApp();
  const [form] = Form.useForm<RuleDraft>();
  const [saving, setSaving] = useState(false);
  const trigger = Form.useWatch("trigger", form) ?? draft.trigger;

  async function save(values: RuleDraft) {
    setSaving(true);
    try {
      if (id) {
        await api.put(`/api/console/proactive/rules/${id}`, values);
      } else {
        await api.post("/api/console/proactive/rules", values);
      }
      message.success("Kural kaydedildi");
      onSaved();
    } catch (e) {
      message.error(errorText(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open title={id ? "Kuralı düzenle" : "Yeni kural"} okText="Kuralı kaydet" cancelText="Vazgeç"
      confirmLoading={saving} onCancel={onClose} onOk={() => form.submit()} destroyOnHidden>
      <Form form={form} layout="vertical" initialValues={draft} onFinish={save}>
        <Form.Item label="Ad" name="name" rules={[{ required: true, whitespace: true, max: 80 }]}>
          <Input />
        </Form.Item>
        <Form.Item label="Tetikleyici" name="trigger" rules={[{ required: true }]}>
          <Select options={Object.entries(TRIGGERS).map(([value, label]) => ({ value, label }))} />
        </Form.Item>
        <Form.Item label={trigger === "DWELL" ? "Sayfada kalma süresi (sn)" : "En az kalma süresi (sn)"}
          name="delaySeconds" rules={[{ required: true }]}
          tooltip={trigger === "EXIT_INTENT"
            ? "Çıkış niyeti yalnızca masaüstünde, müşteri sayfada bu süre kaldıktan sonra tetiklenir" : undefined}>
          <InputNumber min={trigger === "DWELL" ? 5 : 0} max={600} />
        </Form.Item>
        <Form.Item label="Sayfalar" name="pageTypes" rules={[{ required: true }]}>
          <Select mode="multiple" options={pageTypes.map((p) => ({ value: p, label: PAGE_LABELS[p] ?? p }))} />
        </Form.Item>
        <Form.Item label="Mesaj" name="message" rules={[{ required: true, whitespace: true, min: 10, max: 160 }]}
          tooltip="Tek cümlelik, baskısız bir soru. Örnek: Bu ürünle ilgili aklınıza takılan bir şey var mı?">
          <Input.TextArea autoSize={{ minRows: 2 }} showCount maxLength={160} />
        </Form.Item>
        <Form.Item label="Etkin" name="enabled" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function LimitsCard({ limits, manage, onChange }: { limits: ProactiveLimits; manage: boolean; onChange: () => void }) {
  const { message } = AntApp.useApp();
  const [saving, setSaving] = useState(false);

  async function save(values: ProactiveLimits) {
    setSaving(true);
    try {
      await api.put("/api/console/proactive/settings", values);
      message.success("Sınırlar kaydedildi");
      onChange();
    } catch (e) {
      message.error(errorText(e));
    } finally {
      setSaving(false);
    }
  }

  if (!manage) {
    return (
      <Card title="Sıklık sınırları">
        <Descriptions column={1} size="small" items={[
          { key: "c", label: "Kapatınca bekleme", children: `${limits.cooldownHours} saat` },
          { key: "s", label: "Oturumda en fazla", children: limits.maxPerSession },
          { key: "d", label: "Ziyaretçiye 24 saatte en fazla", children: limits.maxPerDay },
        ]} />
      </Card>
    );
  }
  return (
    <Card title="Sıklık sınırları">
      <Form layout="inline" initialValues={limits} onFinish={save}>
        <Form.Item label="Kapatınca bekleme (saat)" name="cooldownHours" rules={[{ required: true }]}>
          <InputNumber min={1} max={720} />
        </Form.Item>
        <Form.Item label="Oturumda en fazla" name="maxPerSession" rules={[{ required: true }]}>
          <InputNumber min={1} max={3} />
        </Form.Item>
        <Form.Item label="Ziyaretçiye 24 saatte en fazla" name="maxPerDay" rules={[{ required: true }]}>
          <InputNumber min={1} max={10} />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={saving}>Kaydet</Button>
      </Form>
    </Card>
  );
}
